import {
  Avatar,
  Badge,
  Box,
  Button,
  DataTable,
  FlexBox,
  IconButton,
  Page,
  Skeleton,
  Text,
  Tooltip,
} from '@dixa/lib-shared-habla';
import { sizes } from '@dixa/lib-shared-tokens';
import { ampli } from 'lib-shared-tracking';
import { User } from 'lib-shared-types-api';
import { ReadRoleUsersApiResponse } from 'lib-shared-user-role';
import { useEffect, useState } from 'react';

import { rolesApi } from 'apis/roles';
import { usersApi } from 'apis/users';
import { appActions } from 'app/app.actions';
// eslint-disable-next-line no-restricted-imports
import { AgentsAddModal } from 'components/shared/agentsAddModal';
import { NotificationType, useNotifications } from 'hooks/useNotifications';
import { useLazyPaginatedEndpoint } from 'utils/apis/pagination/useLazyPaginatedEndpoint';
import { useAppDispatch } from 'utils/redux/useAppDispatch';
import { useAppPathParams } from 'utils/url/useAppPathParams';
import { getUserDisplayName } from 'utils/user';

export const SettingsAgentsRolesEditAgentsView = (): JSX.Element => {
  const dispatch = useAppDispatch();
  const notify = useNotifications();

  const { roleId, roleType } = useAppPathParams(
    '/settings/agents/roles/:roleType/:roleId/agents',
  );

  const [isAddModalOpen, setIsAddModalOpen] = useState(false);

  const {
    userIds,
    isLoading: isLoadingUserIds,
    isSuccess,
    isError,
    refetch,
  } = rolesApi.useReadRoleUsersQuery(
    { roleId },
    {
      selectFromResult: ({ data, ...rest }) => ({
        ...rest,
        userIds: data?.data.user_ids ?? [],
      }),
    },
  );

  const {
    items: users,
    asyncStatus,
    changeArg,
    removeItem,
  } = useLazyPaginatedEndpoint({
    endpoint: usersApi.endpoints.readUsers,
    itemsSelector: ({ data: { users } }) => users,
  });

  const isLoading = isLoadingUserIds || asyncStatus === 'pending';

  useEffect(() => {
    if (userIds.length) {
      changeArg({
        // TODO [https://dixa-product.atlassian.net/browse/CORE-512]
        // This is a temporary implementation using /v1/users endpoint that supports only 100 ids at
        // at time, and will be replaced by /v2/users endpoint with proper pagination support.
        userIds: userIds.slice(0, 100),
        limit: 100,
        offset: 0,
      });
    }
  }, [changeArg, userIds]);

  const [updateRoleUsers] = rolesApi.useUpdateRoleUsersMutation();
  const [updateUserRoles] = usersApi.useUpdateUserRolesMutation();

  const handleAddUsers = async (
    newUserIds: string[],
  ): Promise<ReadRoleUsersApiResponse> => {
    const userAdditions = newUserIds.filter((id) => !userIds.includes(id));

    await updateRoleUsers({
      roleId,
      userAdditions,
      userDeletions: [],
    });

    ampli.settingsRolesUsersAssigned({
      numberOfUsers: userAdditions.length,
      roleType,
    });

    return refetch().unwrap();
  };

  const handleRemoveUser = async (user: User) => {
    const name = getUserDisplayName(user);

    const { isConfirmed, confirm } = await dispatch(
      appActions.showModal({
        modal: {
          type: 'app:confirm',
          arg: {
            title: `Are you sure?`,
            description: `Are you sure you want to remove "${name}" agent from this role?`,
            confirm: {
              label: 'Remove',
              async: true,
              asyncLabel: 'Removing...',
            },
          },
        },
      }),
    );

    if (isConfirmed) {
      try {
        confirm(async () => {
          await updateUserRoles({
            userId: user.id,
            roleAdditions: [],
            roleDeletions: [roleId],
          }).unwrap();

          notify(
            {
              title: 'Agent removed',
              text: `"${name}" has been removed from the role.`,
              icon: 'i-done',
              time: 3000,
            },
            NotificationType.Success,
          );

          ampli.settingsRolesUserUnassigned({ roleType });

          await refetch().unwrap();

          removeItem(user, (a, b) => a.id === b.id);
        });
      } catch {
        notify(
          {
            title: 'Error while removing the agent from the role.',
          },
          NotificationType.Error,
        );
      }
    }
  };

  const total = userIds.length;

  return (
    <>
      <Page.Body>
        <Page.Section
          title="All agents"
          titleMeta={
            !!total && (
              <Badge size="small" variant="secondary">
                {total}
              </Badge>
            )
          }
          controls={
            <>
              <Button onClick={() => setIsAddModalOpen(true)}>
                Add agents
              </Button>
            </>
          }
        >
          <DataTable
            columns={[
              {
                label: 'Agent',
                selector: (user) => {
                  if (isLoading) {
                    return (
                      <FlexBox alignItems="center" gap="300">
                        <Skeleton
                          width={sizes.spacer[500]}
                          height={sizes.spacer[500]}
                        />

                        <Skeleton height="14px" width="50%" />
                      </FlexBox>
                    );
                  }

                  return (
                    <FlexBox alignItems="center" gap="300">
                      <Box
                        width={sizes.spacer[500]}
                        minWidth={sizes.spacer[500]}
                      >
                        <Avatar user={user} />
                      </Box>

                      <Text.Paragraph>
                        {getUserDisplayName(user)}
                      </Text.Paragraph>
                    </FlexBox>
                  );
                },
                style: { width: '50%' },
              },
              {
                label: 'Email',
                selector: (user) => {
                  if (isLoading) {
                    return <Skeleton height="14px" width="50%" />;
                  }
                  return user.email;
                },
                style: { width: '50%' },
              },
              {
                label: '',
                selector: (user) => {
                  if (isLoading) {
                    return (
                      <FlexBox
                        justifyContent="center"
                        alignItems="center"
                        width="32px"
                        height="32px"
                      >
                        <Skeleton height="18px" width="18px" />
                      </FlexBox>
                    );
                  }

                  return (
                    <Tooltip content="Remove agent from this role" dark>
                      <IconButton
                        variant="tertiary"
                        icon="trash"
                        onClick={() => handleRemoveUser(user)}
                      />
                    </Tooltip>
                  );
                },
                style: {
                  width: 0,
                },
              },
            ]}
            data={
              isLoading ? (Array.from({ length: 5 }) as typeof users) : users
            }
            tableStatusProps={{
              loadingFailed: isError,
              onLoadingRetry: refetch,
              emptyState:
                !userIds.length && isSuccess
                  ? {
                      title: 'No agents added',
                      description:
                        'Click on "Add agents" to add one or more agents to this role.',
                    }
                  : undefined,
            }}
            tableStyleProps={{
              stickyHeader: true,
              stickyFooter: true,
            }}
          />
        </Page.Section>
      </Page.Body>

      {isAddModalOpen && (
        <AgentsAddModal
          isOpen
          onClose={() => setIsAddModalOpen(false)}
          addAgents={handleAddUsers}
          parentEntity="role"
        />
      )}
    </>
  );
};
